import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import type { DashboardCustomer } from '@/contexts/DashboardCustomerContext';

interface CustomerPickerProps {
  /** Every customer the picker can offer, in display order. */
  customers: DashboardCustomer[];
  /** `customer_id` of the current selection; shown in the input while closed. */
  selectedId: string;
  /** Called with the chosen `customer_id` when the user picks a different customer. */
  onChange: (id: string) => void;
}

/**
 * Accessible customer combobox for the dashboard header.
 *
 * Type to filter by customer name or ID, use ArrowUp/ArrowDown to move the
 * highlight, Enter to choose and Escape, Tab or tapping outside to close without
 * changing the selection. The query is cleared whenever the list closes so the
 * input always falls back to showing the selected customer's name; typing again
 * starts a fresh query rather than appending to that name.
 */
export function CustomerPicker({ customers, selectedId, onChange }: CustomerPickerProps) {
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const selected = customers.find(customer => customer.customer_id === selectedId);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return customers;
    return customers.filter(customer => `${customer.customer_name} ${customer.customer_id}`.toLocaleLowerCase().includes(needle));
  }, [customers, query]);

  // Highlight the current selection when opening; the first match while filtering.
  useEffect(() => {
    if (open) setActive(Math.max(0, filtered.findIndex(customer => customer.customer_id === selectedId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { if (query) setActive(0); }, [query]);

  const close = () => { setOpen(false); setQuery(''); };
  const choose = (customer: DashboardCustomer) => { onChange(customer.customer_id); close(); };

  // Once closed, the input shows the selected name again. Select it while it
  // still has focus so a keystroke replaces the name even if the browser
  // inserted the character before React reset the value.
  useEffect(() => {
    if (!open && document.activeElement === inputRef.current) inputRef.current?.select();
  }, [open]);

  // pointerdown rather than mousedown: iOS Safari does not synthesise mouse
  // events for taps on non-clickable content.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) close(); };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const isTypingKey = (event: React.KeyboardEvent<HTMLInputElement>) =>
    event.key === 'Backspace' || event.key === 'Delete' ||
    (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && isTypingKey(event)) {
      // Start a fresh query instead of appending to the selected name. No
      // preventDefault: React flushes this state before the character is
      // inserted, so the following onChange sees only the new keystroke.
      setQuery('');
      setOpen(true);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true); else setActive(index => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      if (open && filtered[active]) { event.preventDefault(); choose(filtered[active]); }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      close();
    }
  };

  const activeCustomer = open ? filtered[active] : undefined;

  return (
    <div ref={root} className="relative w-full min-w-0 max-w-xs sm:max-w-sm lg:w-full">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <input
        ref={inputRef}
        role="combobox" aria-label="Customer" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={activeCustomer ? `${listId}-${activeCustomer.customer_id}` : undefined}
        type="text" value={open ? query : selected?.customer_name ?? ''}
        placeholder={selected ? selected.customer_name : 'Select a customer'}
        onFocus={() => setOpen(true)} onClick={() => setOpen(true)}
        onChange={event => { setQuery(event.target.value); setOpen(true); }} onKeyDown={onKeyDown}
        className="w-full pl-9 pr-8 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-gray-50 dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5869ff]"
      />
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      {open && (
        <div className="absolute z-40 mt-1 w-full rounded-lg border border-gray-200 dark:border-[#5869ff]/20 bg-white dark:bg-[#000742] shadow-lg">
          <ul id={listId} role="listbox" aria-label="Customers" className="max-h-72 overflow-y-auto py-1">
            {filtered.map((customer, index) => (
              <li key={customer.customer_id} id={`${listId}-${customer.customer_id}`} role="option"
                aria-selected={customer.customer_id === selectedId}
                onMouseDown={event => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(customer)}
                className={`px-3 py-2 text-sm cursor-pointer text-gray-900 dark:text-white ${index === active ? 'bg-[#5869ff]/10 dark:bg-[#5869ff]/20' : ''} ${customer.customer_id === selectedId ? 'font-semibold' : ''}`}>
                {customer.customer_name} <span className="text-gray-500 dark:text-gray-400">({customer.customer_id})</span>
              </li>
            ))}
          </ul>
          <p role="status" className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-[#5869ff]/20">
            {filtered.length === 0 ? 'No matching customers' : `${filtered.length} of ${customers.length} customers`}
          </p>
        </div>
      )}
    </div>
  );
}
